import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LogbookEntry } from '../types/logbook';
import {
  getAllLogbookEntries,
  createLogbookEntry,
  updateLogbookEntry,
  type LogbookEntryCreateDto,
  type LogbookEntryPatchDto,
} from '../lib/api/endpoints';
import { logbookEntryFromDto } from '../lib/api/mappers';

export interface LogbookFilter {
  entryTypeId?: string;
  vehicleId?: string;
  driverId?: string;
  tripId?: string;
  subunitId?: string;
  /** ⚠️ Filtran por `occurredAt` — cuándo OCURRIÓ, no cuándo se digitó. */
  from?: string;
  to?: string;
}

/**
 * FE-13 — el timeline de novedades y las dos operaciones que lo mueven.
 *
 * La visibilidad la aplica el servidor (§4.8): el Operador ve solo las suyas,
 * el Supervisor las de su sub-unidad —por la del vehículo **o** la del
 * conductor, porque una sanción de tránsito no tiene vehículo—, Administrador
 * y Ejecutivo el consolidado. Este hook NO vuelve a filtrar por rol ni por
 * tenant: hacerlo en el cliente sería fingir una autorización que no tenemos
 * (regla de oro del contrato §7).
 *
 * `registrar` y `corregir` devuelven la novedad ya hidratada por el servidor y
 * la reemplazan en la lista sin recargarla entera. Lanzan `ApiError` tal cual:
 * el `403` del Ejecutivo y el `404` de una novedad ajena son información de
 * negocio.
 */
export function useLogbookEntries(filter: LogbookFilter = {}) {
  const [entries, setEntries] = useState<LogbookEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // El filtro llega como objeto literal desde el componente: sin esto, cada
  // render dispararía una carga nueva.
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(filterKey) as LogbookFilter;

    const load = async () => {
      setIsLoading(true);
      try {
        const items = await getAllLogbookEntries(parsed);
        if (cancelled) return;
        setEntries(items.map(logbookEntryFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setEntries([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando las novedades'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [filterKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  // El listado llega "la más reciente primero" por `occurredAt`, y así se
  // conserva: reemplazar en sitio no cambia el orden que decidió el servidor.
  // Una novedad NUEVA sí se reordena, porque su `occurredAt` puede ser de
  // anteayer — encabezar la lista con ella la pondría donde no va.
  const upsert = useCallback((entry: LogbookEntry) => {
    setEntries(prev => {
      const idx = prev.findIndex(e => e.id === entry.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = entry;
        return next;
      }
      return [...prev, entry].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
    });
  }, []);

  const acciones = useMemo(() => ({
    registrar: async (data: LogbookEntryCreateDto): Promise<LogbookEntry> => {
      const entry = logbookEntryFromDto(await createLogbookEntry(data));
      upsert(entry);
      return entry;
    },
    // Corregir el `occurredAt` puede sacar la novedad del expediente de un
    // viaje y meterla en el de otro (ADR-024): quien muestre un expediente
    // tiene que volver a pedirlo.
    corregir: async (entryId: string, data: LogbookEntryPatchDto): Promise<LogbookEntry> => {
      const entry = logbookEntryFromDto(await updateLogbookEntry(entryId, data));
      upsert(entry);
      return entry;
    },
  }), [upsert]);

  return { entries, isLoading, error, reload, ...acciones };
}
